/**
 * /ambassador/claim — ONE-STEP landing for an invited ambassador.
 *
 * The claim token in the URL IS the credential (it was emailed to the
 * invitee — same trust model as a magic link), so this page NEVER
 * redirects to /login. Flow:
 *   1. They arrive from email with ?token=...
 *   2. GET /ambassador/claim/info → invite preview (email, suggested
 *      handle, rate, whether an account already exists for that email).
 *   3. One form: handle + payout (+ optional password when they have
 *      no account yet). Submit → POST /ambassador/claim/complete →
 *      account created-or-matched, ambassador role attached, session
 *      cookie set → straight to /ambassador.
 *
 * Previously we bounced logged-out invitees to /login, which dropped
 * the token and stranded them with a roleless account.
 *
 * Fully localised via `ambassador.claim.*` keys (en/es/fr/pt).
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, Sparkles, Heart, ArrowRight, Mail } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  API,
  getAmbassadorClaimInfo,
  completeAmbassadorClaim,
} from "../lib/api";

const DEFAULT_RATE_PCT = 30;

// Shared card frame so the form and every invite state (invalid,
// expired, claimed) render inside the same branded shell.
const Shell = ({ children }) => (
  <div
    className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-5 py-10"
    data-testid="ambassador-claim-page"
  >
    <div className="w-full max-w-md bg-white border border-[#EADDD7] rounded-3xl p-7 shadow-[0_20px_50px_-18px_rgba(155,106,108,0.20)]">
      <div className="flex items-center gap-2.5 mb-2">
        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C] flex items-center justify-center">
          <Sparkles className="h-4 w-4 text-white" />
        </div>
        <span className="font-serif text-xl tracking-tight text-[#2C2123]">
          TruBeauté
        </span>
      </div>
      {children}
    </div>
  </div>
);

const AmbassadorClaim = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null); // null = loading
  const [handle, setHandle] = useState("");
  const [payoutMethod, setPayoutMethod] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [taxNote, setTaxNote] = useState("");
  const [password, setPassword] = useState("");
  const [tosVersion, setTosVersion] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  // Invite preview — drives which state renders and prefills the form.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getAmbassadorClaimInfo(token);
        if (cancelled) return;
        setInfo(data);
        if (data.status === "valid") {
          if (data.handle_suggested) setHandle(data.handle_suggested);
          if (data.name) setDisplayName(data.name);
        }
      } catch {
        if (!cancelled) setInfo({ status: "invalid" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Same public ToS-version fetch Login.jsx uses, so the acceptance we
  // stamp on the new account carries the real version string.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/legal/tos-version`);
        if (!cancelled) setTosVersion(data?.current_version || null);
      } catch {
        if (!cancelled) setTosVersion("unknown");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6">
        <p className="text-[#5C4B51]" data-testid="ambassador-claim-missing-token">
          {t("ambassador.claim.missingToken")}
        </p>
      </div>
    );
  }
  if (info === null) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#9B6A6C]" />
      </div>
    );
  }
  if (info.status === "invalid" || info.status === "expired") {
    return (
      <Shell>
        <p
          className="text-[#5C4B51] mt-3"
          data-testid={`ambassador-claim-${info.status}`}
        >
          {t(
            info.status === "expired"
              ? "ambassador.claim.errorExpired"
              : "ambassador.claim.errorInvalid",
          )}
        </p>
        <p className="text-[11px] text-[#8A7D82] mt-5">
          <Link to="/" className="underline">
            {t("ambassador.claim.backCta")}
          </Link>
        </p>
      </Shell>
    );
  }
  if (info.status === "claimed") {
    return (
      <Shell>
        <h1
          className="font-serif italic text-2xl text-[#2C2123] leading-tight mt-2 mb-2"
          data-testid="ambassador-claim-already-claimed"
        >
          {t("ambassador.claim.alreadyClaimedHeadline")}
        </h1>
        <p className="text-sm text-[#5C4B51] mb-5">
          {t("ambassador.claim.alreadyClaimedBody")}
        </p>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 bg-[#9B6A6C] text-white px-5 py-2.5 rounded-full hover:bg-[#805557] transition-colors font-medium text-sm"
          data-testid="ambassador-claim-signin-cta"
        >
          {t("ambassador.claim.signInCta")}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </Shell>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      const res = await completeAmbassadorClaim({
        token,
        handle: handle.trim().toLowerCase(),
        payout_method: payoutMethod.trim(),
        tax_note: taxNote.trim() || null,
        display_name: displayName.trim() || null,
        password: info.account_exists ? undefined : password || undefined,
        tos_version: tosVersion || "unknown",
      });
      setUser(res.user);
      toast.success(t("ambassador.claim.successHeadline"));
      navigate("/ambassador", { replace: true });
    } catch (e2) {
      const code = e2?.response?.data?.detail;
      const errorKeys = {
        invalid_token: "ambassador.claim.errorInvalid",
        expired: "ambassador.claim.errorExpired",
        already_claimed: "ambassador.claim.errorAlreadyClaimed",
        email_mismatch: "ambassador.claim.errorEmailMismatch",
        handle_taken: "ambassador.claim.errorHandleTaken",
        weak_password: "ambassador.claim.errorWeakPassword",
      };
      const friendly = errorKeys[code]
        ? t(errorKeys[code])
        : t("ambassador.claim.errorGeneric");
      setErr(friendly);
    } finally {
      setSubmitting(false);
    }
  };

  const ratePct = info.commission_rate
    ? Math.round(info.commission_rate * 100)
    : DEFAULT_RATE_PCT;
  const handlePreview = handle || "you";
  const couponPreview = handlePreview.toUpperCase();

  return (
    <Shell>
      <h1
        className="font-serif italic text-3xl text-[#2C2123] leading-tight mb-1"
        data-testid="ambassador-claim-headline"
      >
        {t("ambassador.claim.headline")}
      </h1>
      <p
        className="text-sm text-[#5C4B51] mb-3"
        data-testid="ambassador-claim-subline"
      >
        {t("ambassador.claim.subline", { ratePct })}
      </p>

      <p
        className="inline-flex items-center gap-1.5 text-xs text-[#5D4050] bg-[#FBF4EF] border border-[#EADDD7] rounded-full px-3 py-1.5 mb-5"
        data-testid="ambassador-claim-email"
      >
        <Mail className="h-3.5 w-3.5 text-[#9B6A6C]" />
        {t("ambassador.claim.claimingAs", { email: info.email })}
      </p>

      {info.account_exists && (
        <p
          className="text-xs text-[#5C4B51] bg-[#F4EDE6] rounded-lg p-3 mb-4"
          data-testid="ambassador-claim-existing-note"
        >
          {t("ambassador.claim.existingAccountNote")}
        </p>
      )}

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C4B51]">
            {t("ambassador.claim.handleLabel")}
          </label>
          <div className="relative mt-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7D82]">
              @
            </span>
            <input
              required
              value={handle}
              onChange={(e) =>
                setHandle(
                  e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9-]/g, "")
                    .slice(0, 24),
                )
              }
              className="w-full pl-7 pr-3 py-2.5 rounded-lg border border-[#EADDD7] bg-[#FAF5F1] focus:border-[#9B6A6C] outline-none"
              placeholder="alex"
              data-testid="ambassador-claim-handle"
            />
          </div>
          <p className="text-[11px] text-[#8A7D82] mt-1">
            <Trans
              i18nKey="ambassador.claim.handlePreview"
              values={{ handle: handlePreview, coupon: couponPreview }}
              components={[<code className="text-[#5D4050]" />]}
            />
          </p>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C4B51]">
            {t("ambassador.claim.payoutLabel")}
          </label>
          <input
            required
            value={payoutMethod}
            onChange={(e) => setPayoutMethod(e.target.value)}
            className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#EADDD7] bg-[#FAF5F1] focus:border-[#9B6A6C] outline-none"
            placeholder={t("ambassador.claim.payoutPlaceholder")}
            data-testid="ambassador-claim-payout-method"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C4B51]">
            {t("ambassador.claim.displayNameLabel")}
          </label>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#EADDD7] bg-[#FAF5F1] focus:border-[#9B6A6C] outline-none"
            placeholder="Alex Rivera"
            data-testid="ambassador-claim-display-name"
          />
        </div>

        {!info.account_exists && (
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C4B51]">
              {t("ambassador.claim.passwordLabel")}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              autoComplete="new-password"
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#EADDD7] bg-[#FAF5F1] focus:border-[#9B6A6C] outline-none"
              placeholder="••••••••"
              data-testid="ambassador-claim-password"
            />
            <p className="text-[11px] text-[#8A7D82] mt-1">
              {t("ambassador.claim.passwordHelp")}
            </p>
          </div>
        )}

        <details className="text-xs text-[#5C4B51]">
          <summary className="cursor-pointer text-[#9B6A6C] hover:underline">
            {t("ambassador.claim.taxLabel")}
          </summary>
          <textarea
            value={taxNote}
            onChange={(e) => setTaxNote(e.target.value)}
            rows={2}
            placeholder={t("ambassador.claim.taxHelp")}
            className="mt-2 w-full px-3 py-2 rounded-lg border border-[#EADDD7] bg-[#FAF5F1] focus:border-[#9B6A6C] outline-none text-sm"
            data-testid="ambassador-claim-tax-note"
          />
        </details>

        {err && (
          <div
            className="text-sm text-[#9B6A6C] bg-[#FBF4EF] border border-[#EADDD7] rounded-lg p-3"
            data-testid="ambassador-claim-error"
          >
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !handle || !payoutMethod}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#9B6A6C] text-white px-5 py-3 rounded-full hover:bg-[#805557] disabled:opacity-50 transition-colors font-medium"
          data-testid="ambassador-claim-submit"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Heart className="h-4 w-4" fill="currentColor" />
          )}
          {submitting
            ? t("ambassador.claim.submitting")
            : t("ambassador.claim.submit")}
          <ArrowRight className="h-4 w-4" />
        </button>
      </form>

      <p className="text-[11px] text-[#8A7D82] mt-4" data-testid="ambassador-claim-tos-note">
        <Trans
          i18nKey="ambassador.claim.tosNote"
          components={[
            <Link to="/terms" className="underline" />,
            <Link to="/privacy" className="underline" />,
          ]}
        />
      </p>

      <p className="text-[11px] text-[#8A7D82] mt-3">
        <Link to="/" className="underline">
          {t("ambassador.claim.backCta")}
        </Link>
      </p>
    </Shell>
  );
};

export default AmbassadorClaim;
